/* eslint-disable camelcase */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@rc/App/Store/Selectors/SettingsSelectors";
import { getScripts } from "@rc/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface ChatStyles {
    container: InjectableCss;
    onlineButton: ClickablePresentationProps;
    offlineButton: ClickablePresentationProps;
}

interface ChatSettings {
    baseLiveAgentContentUrl: string;
    baseLiveAgentUrl: string;
    buttonLabel: string;
    deploymentId: string;
    eswLiveAgentDevName: string;
    organizationId: string;
    scriptUrl: string;
    buttonId: string;
    svcLink1: string;
    svcLink2: string;
    eswConfigDevName: string;
}

const chatStyles: ChatStyles = {
    container: {
        css: css``,
    },
    onlineButton: {
        css: css`
            display: none;
        `,
    },
    offlineButton: {
        css: css`
            display: none;
        `,
    },
};

const styles = chatStyles;
const Chat: React.FC<Props> = ({ id }) => {
    const { chatSetting } = useAppSelector(state => {
        return {
            chatSetting: getSettingsCollection(state).chatSetting,
        };
    });
    const [settingsLoaded, setSettingsLoaded] = React.useState(true);

    React.useEffect(() => {
        if (!chatSetting) {
            return;
        }

        if (chatSetting) {
            for (const key in chatSetting) {
                if (!chatSetting[key as keyof ChatSettings]) {
                    setSettingsLoaded(false);
                    break;
                }
            }
        }

        if (!settingsLoaded) {
            return;
        }

        if (!(window as any)._laq) {
            (window as any)._laq = [];
        }
        (window as any)._laq.push(function () {
            (window as any).liveagent.showWhenOnline(
                `${chatSetting?.buttonId}`,
                document.getElementById(`liveagent_button_online_${chatSetting?.buttonId}`),
            );
            (window as any).liveagent.showWhenOffline(
                `${chatSetting?.buttonId}`,
                document.getElementById(`liveagent_button_offline_${chatSetting?.buttonId}`),
            );
        });
        if (chatSetting !== undefined) {
            getScripts.url(chatSetting.scriptUrl).then(() => {
                const initESW = function (gslbBaseURL: string | null, chatSettings?: ChatSettings) {
                    (window as any).embedded_svc.settings.displayHelpButton = true; // Or false
                    (window as any).embedded_svc.settings.language = "en-US"; // For example, enter 'en' or 'en-US'
                    (window as any).embedded_svc.settings.defaultMinimizedText = chatSetting.buttonLabel; // (Defaults to Chat with an Expert)
                    // (window as any).embedded_svc.settings.disabledMinimizedText = '...'; //(Defaults to Agent Offline)

                    // (window as any).embedded_svc.settings.loadingText = ''; //(Defaults to Loading)
                    // (window as any).embedded_svc.settings.storageDomain = 'yourdomain.com'; //(Sets the domain for your deployment so that visitors can navigate subdomains during a chat session)

                    // Settings for Chat
                    // (window as any).embedded_svc.settings.directToButtonRouting = function(prechatFormData) {
                    // Dynamically changes the button ID based on what the visitor enters in the pre-chat form.
                    // Returns a valid button ID.
                    // };
                    // (window as any).embedded_svc.settings.prepopulatedPrechatFields = {}; //Sets the auto-population of pre-chat form fields
                    // (window as any).embedded_svc.settings.fallbackRouting = []; //An array of button IDs, user IDs, or userId_buttonId
                    (window as any).embedded_svc.settings.offlineSupportMinimizedText = chatSetting.offlineButtonLabel; // (Defaults to Leave Us A Message)

                    (window as any).embedded_svc.settings.enabledFeatures = ["LiveAgent"];
                    (window as any).embedded_svc.settings.entryFeature = "LiveAgent";

                    (window as any).embedded_svc.init(
                        `${chatSettings?.svcLink1}`,
                        `${chatSettings?.svcLink2}`,
                        gslbBaseURL,
                        `${chatSettings?.organizationId}`,
                        `${chatSettings?.eswConfigDevName}`,
                        {
                            baseLiveAgentContentURL: chatSettings?.baseLiveAgentContentUrl,
                            deploymentId: chatSettings?.deploymentId,
                            buttonId: chatSetting?.buttonId,
                            baseLiveAgentURL: chatSettings?.baseLiveAgentUrl,
                            eswLiveAgentDevName: chatSettings?.eswLiveAgentDevName,
                            isOfflineSupportEnabled: true,
                        },
                    );
                };
                setTimeout(() => {
                    if (!(window as any).embedded_svc) {
                        const s = document.createElement(`script`);
                        s.setAttribute(`src`, `${chatSetting.scriptSource}`);
                        s.onload = function () {
                            initESW(null);
                        };
                        document.body.appendChild(s);
                    } else {
                        initESW(`https://service.force.com`, chatSetting);
                    }
                }, 500);
            });
        }
    }, [chatSetting.scriptUrl]);

    if (!settingsLoaded) {
        return null;
    }
    return (
        <StyledWrapper {...styles.container}>
            <Clickable
                // eslint-disable-next-line no-script-url
                href="javascript://Chat"
                id={`liveagent_button_online_${chatSetting?.buttonId}`}
                onClick={() => {
                    (window as any).liveagent.startChat(chatSetting?.buttonId);
                }}
                {...styles.onlineButton}
            />
            <Clickable id={`liveagent_button_offline_${chatSetting?.buttonId}`} {...styles.onlineButton} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Chat,
    definition: {
        displayName: "Chat",
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
